import * as React from "react";
import { useState } from "react";

import * as _ from "lodash";
import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { SearchInput } from "@similarweb/ui-components";

import { ExcelButton } from "app/components/React/ExcelButton/ExcelButton";
import { SWReactTable } from "app/components/React/Table/SWReactTable";
import { i18nFilter } from "app/filters/i18nFilter";

import { countryTextByIdFilter } from "../../../filters/ngFilters";
import { SearchContainer, TableWrapper } from "../../conversion/conversionTableContainer/StyledComponents";

import { segmentsAnalysisGeoTableColumnsConfigGen } from "./SegmentGeographyTableConfig";

interface ISegmentAnalysisGeographyTableProps {
    tableData: any;
    isLoading?: boolean;
    tableExcelUrl?: string;
}

// We use a table top divider as a border,
// since the tableTopContainer bordrer had a bug
// related to boxSizing of the elements within it.
const TableTopDivider = styled.div`
    width: 100%;
    height: 1px;
    background-color: ${colorsPalettes.carbon[100]};
`;

// We disabled the border-top (that is defined in the searchContainer styled component)
// since it has a bug that has to do with box-sizing and elements it contains
// to create an effect of a border.
const TableTopContainer = styled(SearchContainer)`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;

    .SearchInput-container {
        width: 100%;

        > div:first-child {
            top: 13px;
        }

        > .SearchInput {
            height: 48px;
        }
    }
`;

const ExcelButtonContainer = styled.div`
    margin-right: 16px;

    .excel-button-wrapper {
        bottom: 0px;
    }
`;

export const SegmentsGeographyTable = (props: ISegmentAnalysisGeographyTableProps) => {
    const [searchString, setSearchString] = useState("");
    const [sortedColumn, setSortedColumn] = useState<{
        field: string;
        sortDirection: "desc" | "asc";
    }>({
        field: "TrafficShare",
        sortDirection: "desc",
    });
    const onSearch = React.useMemo(
        () => (val: string) => {
            setSearchString(val.trim().toLowerCase());
        },
        [],
    );
    const onSort = React.useMemo(
        () =>
            ({ field, sortDirection }) => {
                setSortedColumn({
                    field,
                    sortDirection,
                });
            },
        [],
    );

    const { isLoading, tableExcelUrl, tableData } = props;
    const filteredData = React.useMemo(() => {
        if (isLoading || !tableData) {
            return [];
        }
        let result =
            !isLoading && searchString !== ""
                ? _.filter(tableData, (dataItem: any) => {
                      return countryTextByIdFilter()(dataItem.Country).toLowerCase().indexOf(searchString) !== -1;
                  })
                : tableData;
        result = orderByWithNullsAtEnd(result, sortedColumn.field, sortedColumn.sortDirection);
        return result;
    }, [isLoading, tableData, searchString, sortedColumn.field, sortedColumn.sortDirection]);

    function orderByWithNullsAtEnd(collection: any[], field: string, sortDirection: "desc" | "asc") {
        if (field === "Country") {
            collection = collection.map((item) => {
                return { ...item, CountryName: countryTextByIdFilter()(item.Country) };
            });
        }
        const partition = _.partition(collection, (x) => !!_.get(x, field, null));
        return _.concat(
            _.orderBy(partition[0], field === "Country" ? "CountryName" : field, sortDirection),
            partition[1],
        );
    }

    const tableProps = {
        onSort,
        isLoading,
        tableData: { Data: filteredData },
        tableColumns: segmentsAnalysisGeoTableColumnsConfigGen(sortedColumn.field, sortedColumn.sortDirection),
    };
    return (
        <TableWrapper loading={isLoading}>
            <TableTopContainer>
                <SearchInput
                    defaultValue={searchString}
                    debounce={100}
                    onChange={onSearch}
                    placeholder={i18nFilter()("segment.analysis.geography.table.search.placeholder")}
                    disableClear={true}
                />
                {tableExcelUrl ? (
                    <ExcelButtonContainer>
                        <ExcelButton
                            url={tableExcelUrl}
                            trackName="segments analysis geography"
                            className={"excel-button-wrapper"}
                        />
                    </ExcelButtonContainer>
                ) : null}
            </TableTopContainer>
            <SWReactTable {...tableProps} />
        </TableWrapper>
    );
};
